import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import './KnowledgeAdmin.css';

// Standalone estimator knowledge-base admin. Mirrors the shapes that
// used to live inside FeedbackPanel, but self-contained: approved rules
// feed every future estimate prompt, and root is the only gate that
// moves a rule from pending → applied (or rejects it).
const API = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/+$/, '');

interface KnowledgeEntry {
  id: string;
  source_estimate_id: string | null;
  content: string;
  status: 'pending' | 'approved' | 'rejected';
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
  decided_by: string | null;
  decided_by_name: string | null;
  decided_at: string | null;
}

const KB_STATUS: Record<KnowledgeEntry['status'], { label: string; cls: string }> = {
  pending: { label: 'Ждёт апрува', cls: 'ka-chip-pending' },
  approved: { label: 'Применяется', cls: 'ka-chip-approved' },
  rejected: { label: 'Отклонено', cls: 'ka-chip-rejected' },
};

interface Toast {
  id: number;
  msg: string;
  kind: 'ok' | 'err';
}

// Server error codes → Russian copy. Falls back to the server's own
// `message`, then a generic line, so nothing surfaces as a raw code.
const ERROR_RU: Record<string, string> = {
  forbidden: 'Недостаточно прав',
  not_found: 'Правило не найдено',
  invalid_content: 'Введите текст правила (1–2000 символов)',
  already_decided: 'Решение по этому правилу уже принято',
};

async function readError(res: Response): Promise<string> {
  const data = await res.json().catch(() => ({} as any));
  return ERROR_RU[data.error as string] || data.message || data.error || `Ошибка (${res.status})`;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const KnowledgeAdmin: React.FC = () => {
  const { user } = useAuth();

  const [entries, setEntries] = useState<KnowledgeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [toasts, setToasts] = useState<Toast[]>([]);

  const pushToast = useCallback((msg: string, kind: 'ok' | 'err') => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, msg, kind }]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4200);
  }, []);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const res = await fetch(`${API}/api/feedback/knowledge`);
      if (!res.ok) {
        setLoadError(await readError(res));
        setEntries([]);
        return;
      }
      const data = await res.json().catch(() => ({} as any));
      setEntries(Array.isArray(data.entries) ? data.entries : []);
    } catch {
      setLoadError('Не удалось загрузить базу знаний — проверьте соединение.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submitRule = async () => {
    const c = content.trim();
    if (!c) {
      pushToast('Введите текст правила', 'err');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/feedback/knowledge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: c }),
      });
      if (!res.ok) {
        pushToast(await readError(res), 'err');
        return;
      }
      const data = await res.json().catch(() => ({} as any));
      const entry: KnowledgeEntry | undefined = data.entry;
      if (entry) setEntries((prev) => [entry, ...prev]);
      else await load();
      setContent('');
      pushToast('Правило добавлено', 'ok');
    } catch {
      pushToast('Сетевая ошибка при отправке правила', 'err');
    } finally {
      setSubmitting(false);
    }
  };

  const decideRule = async (entry: KnowledgeEntry, action: 'approve' | 'reject') => {
    setBusyId(entry.id);
    try {
      const res = await fetch(`${API}/api/feedback/knowledge/${entry.id}/${action}`, {
        method: 'POST',
      });
      if (!res.ok) {
        pushToast(await readError(res), 'err');
        // A 409 means someone else already decided — resync the row.
        if (res.status === 409) await load();
        return;
      }
      const data = await res.json().catch(() => ({} as any));
      const updated: KnowledgeEntry | undefined = data.entry;
      setEntries((prev) => prev.map((e) => (e.id === entry.id ? updated ?? e : e)));
      pushToast(action === 'approve' ? 'Правило применяется' : 'Правило отклонено', 'ok');
    } catch {
      pushToast('Сетевая ошибка при решении', 'err');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="ka">
      <div className="ka-add">
        <textarea
          className="form-textarea ka-input"
          placeholder="Новое правило для оценщика…"
          value={content}
          maxLength={2000}
          disabled={submitting}
          onChange={(e) => setContent(e.target.value)}
        />
        <div className="ka-add-actions">
          <button
            className="btn-primary"
            disabled={submitting || !content.trim()}
            onClick={submitRule}
          >
            {submitting ? 'Отправка…' : 'Добавить правило'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="ka-notice">Загрузка…</div>
      ) : loadError ? (
        <div className="ka-notice ka-notice-err">
          {loadError}
          <button className="btn-secondary ka-retry" onClick={load}>
            Повторить
          </button>
        </div>
      ) : entries.length === 0 ? (
        <div className="ka-empty">Правил пока нет.</div>
      ) : (
        <div className="ka-list">
          {entries.map((k) => {
            const meta = KB_STATUS[k.status];
            const busy = busyId === k.id;
            return (
              <div className="ka-item" key={k.id}>
                <div className="ka-item-top">
                  <div className="ka-content">{k.content}</div>
                  <span className={`ka-chip ${meta.cls}`}>{meta.label}</span>
                </div>
                <div className="ka-item-meta">
                  <span className="ka-author">{k.created_by_name || 'Неизвестно'}</span>
                  <span className="ka-dot">·</span>
                  <span className="ka-date">{fmtDate(k.created_at)}</span>
                  {k.source_estimate_id && (
                    <>
                      <span className="ka-dot">·</span>
                      <span className="ka-source">из оценки</span>
                    </>
                  )}
                  {k.status !== 'pending' && k.decided_by_name && (
                    <>
                      <span className="ka-dot">·</span>
                      <span className="ka-decider">
                        {k.status === 'approved' ? 'применил' : 'отклонил'} {k.decided_by_name}
                      </span>
                    </>
                  )}
                </div>
                {user?.isRoot && k.status === 'pending' && (
                  <div className="ka-actions">
                    <button
                      className="btn-primary ka-btn"
                      disabled={busy}
                      onClick={() => decideRule(k, 'approve')}
                    >
                      {busy ? '…' : 'Применить'}
                    </button>
                    <button
                      className="btn-secondary ka-btn"
                      disabled={busy}
                      onClick={() => decideRule(k, 'reject')}
                    >
                      Отклонить
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {toasts.length > 0 && (
        <div className="ka-toasts">
          {toasts.map((t) => (
            <div key={t.id} className={`ka-toast ka-toast-${t.kind}`}>
              {t.msg}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default KnowledgeAdmin;
